// Ward³ SOC SPA — i18n init.
// Charge react-i18next en singleton (pas de Provider requis). Importé une
// seule fois depuis `main.jsx` ; tout import suivant retourne l'instance
// déjà configurée. Les ressources sont importées en statique (4 fichiers
// × 2 locales = ~5 KB gzippé total) — split asynchrone non nécessaire à
// ce volume.
//
// Stratégie de détection :
//   1. localStorage (`i18nextLng`) — pose par le LanguageSwitcher.
//   2. navigator.language — hint au 1er visiteur non authentifié.
//   3. fallbackLng "en" — défaut produit (ADR i18n).
//
// La locale utilisateur authentifiée vit côté DB (`users.locale`).
// AuthContext appelle `i18n.changeLanguage(user.locale)` après le fetch
// `/auth/me` — c'est le serveur qui gagne sur localStorage dès qu'on est
// connecté. Pour l'écran de login anonyme, localStorage gagne.

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import enCommon from "./locales/en/common.json";
import enNav from "./locales/en/nav.json";
import enAuth from "./locales/en/auth.json";
import enModules from "./locales/en/modules.json";

import frCommon from "./locales/fr/common.json";
import frNav from "./locales/fr/nav.json";
import frAuth from "./locales/fr/auth.json";
import frModules from "./locales/fr/modules.json";

export const SUPPORTED_LOCALES = ["en", "fr"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    // Si la détection retourne `fr-FR` ou `en-GB`, on tronque au code
    // primaire — sinon le lookup retombe sur fallbackLng sans raison.
    load: "languageOnly",
    ns: ["common", "nav", "auth", "modules"],
    defaultNS: "common",
    resources: {
      en: { common: enCommon, nav: enNav, auth: enAuth, modules: enModules },
      fr: { common: frCommon, nav: frNav, auth: frAuth, modules: frModules },
    },
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: "i18nextLng",
      caches: ["localStorage"],
    },
    interpolation: { escapeValue: false },
    // Debug uniquement en `npm run dev`, jamais en tests (vitest set
    // `import.meta.env.VITEST` à true) sinon la sortie des tests est
    // polluée par le dump de la config i18next au boot.
    debug: import.meta.env.DEV && !import.meta.env.VITEST,
    react: { useSuspense: false },
  });

export default i18n;
